import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router";
import { UNAUTHORIZED_EVENT, clearApiCache } from "@/lib/api/client";
import type { Role } from "./access";

export type UserRole = Role;

export type SessionUser = {
  id: string;
  email: string;
  role: UserRole;
  name: string | null;
  image: string | null;
};

type SessionState =
  | { status: "loading"; user: null }
  | { status: "authenticated"; user: SessionUser }
  | { status: "unauthenticated"; user: null };

type SessionContextValue = SessionState & {
  /** Re-read /api/auth/session (e.g. after signing in). */
  refresh: () => Promise<SessionUser | null>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

async function fetchSession(): Promise<SessionUser | null> {
  const res = await fetch("/api/auth/session", {
    credentials: "same-origin",
    headers: { "x-proly-client": "web" },
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`Session check failed (${res.status})`);
  const data = (await res.json()) as { user?: SessionUser };
  return data.user ?? null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading", user: null });

  const refresh = useCallback(async () => {
    try {
      const user = await fetchSession();
      setState(user ? { status: "authenticated", user } : { status: "unauthenticated", user: null });
      return user;
    } catch {
      setState({ status: "unauthenticated", user: null });
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    await fetch("/api/auth/signout", {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-proly-client": "web" },
    }).catch(() => undefined);
    clearApiCache();
    setState({ status: "unauthenticated", user: null });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any API call that comes back 401 means the session expired.
  useEffect(() => {
    const onUnauthorized = () => setState({ status: "unauthenticated", user: null });
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const value = useMemo(() => ({ ...state, refresh, signOut }), [state, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** The signed-in user; only call below <RequireAuth>. */
export function useCurrentUser(): SessionUser {
  const { user } = useSession();
  if (!user) throw new Error("useCurrentUser called without a signed-in user");
  return user;
}

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-slate-500 dark:text-slate-400">
      Loading…
    </div>
  );
}

/** Renders children for signed-in users; otherwise sends them to /login. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (session.status === "loading") return <FullPageSpinner />;
  if (session.status === "unauthenticated") {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?callbackUrl=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

/** Renders children only for platform super-admins; others go home. */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { user } = useSession();
  if (user?.role !== "SUPER_ADMIN") return <Navigate to="/" replace />;
  return <>{children}</>;
}
